import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import Select from "react-select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { FormModal } from "@/components/Settings/FormModal";
import { applicationsApi, usersApi } from "@/apis";
import type { AIApp } from "@/apis";
import { useUserApplicationsStore } from "@/stores/userApplicationsStore";

interface AIAppFormState {
  appName: string;
  description: string;
  icon: string;
  app_url: string;
  backend_url: string;
  route: string;
  usersgroups: string[];
  isActive: boolean;
  isDisabled: boolean;
}

const EMPTY_FORM: AIAppFormState = {
  appName: "",
  description: "",
  icon: "",
  app_url: "",
  backend_url: "",
  route: "",
  usersgroups: [],
  isActive: true,
  isDisabled: false,
};

const stripTrailingSlash = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^https?:\/\/$/i.test(trimmed)) return trimmed;
  return trimmed.replace(/\/+$/, "");
};

const validateHttpUrl = (value: string, fieldLabel: string): string | null => {
  if (!value) return null;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return `${fieldLabel} should start with http:// or https://`;
    }
    return null;
  } catch {
    return `${fieldLabel} should start with http:// or https://`;
  }
};

export function AIAppsManager() {
  const refreshUserApplications = useUserApplicationsStore((state) => state.refreshApplications);
  const [apps, setApps] = useState<AIApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [modalError, setModalError] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editing, setEditing] = useState<AIApp | null>(null);
  const [form, setForm] = useState<AIAppFormState>(EMPTY_FORM);
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState("");
  const [existingIconUrl, setExistingIconUrl] = useState("");
  const [groupOptions, setGroupOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState<AIApp | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const modalTitle = useMemo(() => (editing ? "Edit AI App" : "Create AI App"), [editing]);

  const loadApps = async () => {
    setLoading(true);
    setPageError("");
    try {
      const data = await applicationsApi.listAll();
      setApps(data);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : "Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadApps();
  }, []);

  useEffect(() => {
    const loadGroupOptions = async () => {
      try {
        const groups = await usersApi.cognitoGroups();
        const uniqueGroups = Array.from(new Set(groups)).sort((a, b) => a.localeCompare(b));
        setGroupOptions(uniqueGroups.map((group) => ({ value: group, label: group })));
      } catch {
        setGroupOptions([]);
      }
    };
    void loadGroupOptions();
  }, []);

  useEffect(() => {
    if (!iconFile) {
      setIconPreview("");
      return;
    }
    const previewUrl = URL.createObjectURL(iconFile);
    setIconPreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [iconFile]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setIconFile(null);
    setExistingIconUrl("");
    setModalError("");
    setIsModalOpen(true);
  };

  const openEdit = (app: AIApp) => {
    const usersgroups = Array.isArray(app.usersgroups)
      ? app.usersgroups
      : typeof app.usersgroups === "string"
        ? app.usersgroups
            .split(",")
            .map((group) => group.trim())
            .filter(Boolean)
        : [];
    setEditing(app);
    setForm({
      appName: app.appName || "",
      description: app.description || "",
      icon: app.iconKey || app.icon || "",
      app_url: app.app_url || "",
      backend_url: app.backend_url || "",
      route: app.route || "",
      usersgroups,
      isActive: app.isActive ?? true,
      isDisabled: app.isDisabled ?? false,
    });
    setIconFile(null);
    setExistingIconUrl(app.icon || "");
    setModalError("");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setModalError("");
    setIsModalOpen(false);
  };

  const uploadIconIfNeeded = async (): Promise<string> => {
    if (!iconFile) {
      return form.icon;
    }
    const upload = await applicationsApi.uploadIcon(iconFile);
    return upload.icon_key;
  };

  const saveApp = async () => {
    const normalizedDescription = form.description.trim();
    const normalizedRoute = form.route.trim();
    const normalizedAppUrl = stripTrailingSlash(form.app_url);
    const normalizedBackendUrl = stripTrailingSlash(form.backend_url);
    const hasIcon = Boolean(iconFile || form.icon || existingIconUrl);

    if (!form.appName.trim()) {
      setModalError("App Name is required.");
      return;
    }

    if (!normalizedDescription) {
      setModalError("Description is required.");
      return;
    }

    if (!normalizedRoute) {
      setModalError("Route is required.");
      return;
    }

    if (!form.usersgroups.length) {
      setModalError("At least one Allowed Group is required.");
      return;
    }

    if (!normalizedAppUrl) {
      setModalError("Application URL is required.");
      return;
    }

    if (!normalizedBackendUrl) {
      setModalError("Backend URL is required.");
      return;
    }

    if (!hasIcon) {
      setModalError("Icon Image is required.");
      return;
    }

    const appUrlError = validateHttpUrl(normalizedAppUrl, "Application URL");
    if (appUrlError) {
      setModalError(appUrlError);
      return;
    }

    const backendUrlError = validateHttpUrl(normalizedBackendUrl, "Backend URL");
    if (backendUrlError) {
      setModalError(backendUrlError);
      return;
    }

    const normalizedForm: AIAppFormState = {
      ...form,
      description: normalizedDescription,
      app_url: normalizedAppUrl,
      backend_url: normalizedBackendUrl,
      route: normalizedRoute,
    };

    setForm(normalizedForm);
    setIsSaving(true);
    setModalError("");
    try {
      const iconUrl = await uploadIconIfNeeded();
      const { appName, ...formWithoutAppName } = normalizedForm;
      let payload: any = {
        ...normalizedForm,
        icon: iconUrl,
      };

      if (editing) {
        payload = {
          ...formWithoutAppName,
          icon: iconUrl,
        };
        await applicationsApi.update(editing.appName, payload);
      } else {
        await applicationsApi.create(payload);
      }

      setIsModalOpen(false);
      setIconFile(null);
      setExistingIconUrl("");
      await loadApps();
      await refreshUserApplications();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Failed to save application");
    } finally {
      setIsSaving(false);
    }
  };

  const openDeleteConfirm = (app: AIApp) => {
    setAppToDelete(app);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (isDeleting) return;
    setDeleteConfirmOpen(false);
    setAppToDelete(null);
  };

  const confirmDelete = async () => {
    if (!appToDelete) return;

    setIsDeleting(true);
    setPageError("");
    try {
      await applicationsApi.remove(appToDelete.appName);
      setDeleteConfirmOpen(false);
      setAppToDelete(null);
      await loadApps();
      await refreshUserApplications();
    } catch (err) {
      setPageError(err instanceof Error ? err.message : "Failed to delete application");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xl">AI Apps</CardTitle>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add AI App
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {pageError ? (
          <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {pageError}
          </div>
        ) : null}

        {loading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
          </div>
        ) : null}

        {!loading && apps.length === 0 ? (
          <p className="text-sm text-muted-foreground">No applications found.</p>
        ) : null}

        {!loading && apps.length > 0 ? (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">App Name</th>
                  <th className="px-3 py-2 text-left font-medium">Route</th>
                  <th className="px-3 py-2 text-left font-medium">usersgroups</th>
                  <th className="px-3 py-2 text-left font-medium">Icon</th>
                  <th className="px-3 py-2 text-left font-medium">Active</th>
                  <th className="px-3 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((app) => (
                  <tr key={app.id} className="border-t">
                    <td className="px-3 py-2">{app.appName}</td>
                    <td className="px-3 py-2">{app.route}</td>
                    <td className="px-3 py-2">
                      {Array.isArray(app.usersgroups) && app.usersgroups.length > 0
                        ? app.usersgroups.join(", ")
                        : "-"}
                    </td>
                    <td className="px-3 py-2">
                      {app.icon ? (
                        <img
                          src={app.icon}
                          alt={`${app.appName} icon`}
                          className="h-8 w-8 rounded object-cover"
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-3 py-2">{app.isActive ? "Yes" : "No"}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEdit(app)}>
                          <Pencil className="mr-2 h-3.5 w-3.5" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDeleteConfirm(app)}
                          disabled={isDeleting}
                        >
                          <Trash2 className="mr-2 h-3.5 w-3.5" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </CardContent>

      <FormModal
        title={modalTitle}
        description="Manage AI application details and groups."
        isOpen={isModalOpen}
        isSaving={isSaving}
        onClose={closeModal}
        onSubmit={saveApp}
      >
        {modalError ? (
          <div className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700">
            {modalError}
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1 md:col-span-2">
            <label className="text-sm font-medium">App Name *</label>
            <Input
              value={form.appName}
              onChange={(e) => setForm((prev) => ({ ...prev, appName: e.target.value }))}
              placeholder="Chatbot App"
            />
          </div>

          <div className="space-y-1 md:col-span-2">
            <label className="text-sm font-medium">Description *</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              rows={3}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Route *</label>
            <Input
              value={form.route}
              onChange={(e) => setForm((prev) => ({ ...prev, route: e.target.value }))}
              placeholder="/chatbot"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Allowed Groups *</label>
            <Select
              isMulti
              options={groupOptions}
              value={groupOptions.filter((opt) => form.usersgroups.includes(opt.value))}
              onChange={(selected) =>
                setForm((prev) => ({
                  ...prev,
                  usersgroups: Array.isArray(selected) ? selected.map((item) => item.value) : [],
                }))
              }
              placeholder="Select usersgroups"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Application URL *</label>
            <Input
              value={form.app_url}
              onChange={(e) => setForm((prev) => ({ ...prev, app_url: e.target.value }))}
              onBlur={(e) =>
                setForm((prev) => ({ ...prev, app_url: stripTrailingSlash(e.target.value) }))
              }
              placeholder="https://example.app"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Backend URL *</label>
            <Input
              value={form.backend_url}
              onChange={(e) => setForm((prev) => ({ ...prev, backend_url: e.target.value }))}
              onBlur={(e) =>
                setForm((prev) => ({ ...prev, backend_url: stripTrailingSlash(e.target.value) }))
              }
              placeholder="https://example.app"
            />
          </div>

          <div className="space-y-1 md:col-span-2">
            <label className="text-sm font-medium">Icon Image *</label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => setIconFile(e.target.files?.[0] ?? null)}
            />
            {(iconFile || form.icon) && (
              <img
                src={iconPreview || existingIconUrl}
                alt="Icon preview"
                className="mt-2 h-12 w-12 rounded object-cover"
              />
            )}
          </div>

          <div className="flex items-center gap-2 pt-2 md:col-span-2">
            <input
              id="isActive"
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
            />
            <label htmlFor="isActive" className="text-sm font-medium">
              Active
            </label>
            <input
              id="isDisabled"
              type="checkbox"
              checked={form.isDisabled}
              onChange={(e) => setForm((prev) => ({ ...prev, isDisabled: e.target.checked }))}
            />
            <label htmlFor="isDisabled" className="text-sm font-medium">
              Disable
            </label>
          </div>
        </div>
      </FormModal>

      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-sm">
            <CardHeader>
              <CardTitle>Delete Application</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 flex flex-col justify-between h-full pb-6">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <strong>{appToDelete?.appName}</strong>? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={closeDeleteConfirm} disabled={isDeleting}>
                  Cancel
                </Button>
                <Button
                  onClick={confirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Card>
  );
}
